import axios from "../axios";
import authAxios from "../authAxios";
import unauthAxios from "../axios";
import { useIsAuthenticated } from "../utils/auth";

export const ensureUserLoggedIn = () => authAxios.get("/v1/user/ensureUserLoggedIn");

export const giveUserWelcomeDiscount = () => authAxios.get("/v1/user/giveUserWelcomeDiscount");

// Background check driving the welcome-gift surfaces — must never log the
// user out on a stale token, hence skipAuthRedirect.
export const getWelcomePromoEligibility = () =>
  authAxios.get("/v1/user/welcomePromoEligibility", { skipAuthRedirect: true });

// Accepts an optional axios config so callers can pass `{ skipAuthRedirect: true }`
// for non-critical background refreshes that must NOT log the user out on a 401
// (see Book.js credit-balance refresh + authAxios response interceptor).
export const getUserData = (config) => authAxios.get("/v1/user/getUser", config);

export const getUserById = (user_id) =>
  axios.get("/v1/user/getUserById", {
    params: { user_id: user_id },
  });

export const getUserByEmail = (email) =>
  axios.get("/v1/user/getUserByEmail", {
    params: { email: email },
  });

export const getUserByPhoneNumber = (ext, number) =>
  axios.get("/v1/user/getUserByPhoneNumber", {
    params: { ext: ext, number: number },
  });

export const getUserByTeleUsername = (tele_username) =>
  axios.get("/v1/user/getUserByTeleUsername", {
    params: { tele_username: tele_username },
  });

// Check if user is authenticated, if it's not authAxios will redirect user
export const useAuthGuard = () => {
  const { isAuthenticated, isLoading } = useIsAuthenticated();

  if (!isLoading && !isAuthenticated) {
    const originalPath = window.location.pathname + window.location.search;
    window.location = `/login?reroute=${originalPath}`;
    return;
  }
};

export const pingLarry = async (trip_name) => {
  await authAxios.get(`/v1/admin/pingLarry?trip_name=${trip_name}`);
  return;
};

export const getUserHostType = async () => {
  const res = await authAxios.get("/v1/user/getUserHostType");
  return res.data;
};

export const updateUser = (update_data) => {
  return authAxios.post("/v1/user/updateUser", update_data);
};

export const updateUserAffiliateCode = (affiliateCode, campaignSlug = null) => {
  const payload = {
    affiliate_code: affiliateCode,
  };

  if (campaignSlug) {
    payload.campaign_slug = campaignSlug;
  }

  return authAxios.post("/v1/user/updateUserAffiliateCode", payload);
};

export const updateUserPartnerCode = (partnerCode) => {
  return authAxios.post("/v1/user/updateUserPartnerCode", {
    partner_code: partnerCode,
  });
};

export const updateProfileUserInterestsAndNotifications = (interests = [], notifications = []) => {
  return authAxios.post("/v1/user/updateProfileUserInterestsAndNotifications", {
    interests: interests,
    notifications: notifications,
  });
};

export const getUserExtraMedia = (user_id) => {
  return unauthAxios.get(`/v1/user/getUserExtraMedia`, {
    params: {
      user_id: user_id,
    },
  });
};

export const updateUserExtraMedia = (data) => {
  return authAxios.post("/v1/user/updateUserExtraMedia", data);
};

export const getUserReviews = (user_id) => {
  return unauthAxios.get("/v1/user/getUserReviews", {
    params: {
      user_id: user_id,
    },
  });
};

export const upsertUserReview = (review_data) => {
  return authAxios.post("/v1/user/upsertUserReview", review_data);
};

export const sendWhatsappOTPForLogin = (ext, number) => {
  return unauthAxios.post("/v1/user/sendWhatsappOTPForLogin", {
    ext,
    number,
  });
};

export const loginWithWhatsapp = (ext, number, otp, user) => {
  return unauthAxios.post("/v1/user/loginWithWhatsapp", {
    ext: ext,
    number: number,
    otp: otp,
    existingUserToDelete: user,
  });
};

export const updateUserPersonalInfo = (data) => {
  return authAxios.post("/v1/user/updateUserPersonalInfo", data);
};

export const sendWelcomeGiftEmail = (data) => {
  return authAxios.post("/v1/user/sendWelcomeGiftEmail", data);
};

export const getLineFriendStatus = () => {
  return authAxios.get("/v1/user/getLineFriendStatus");
};

// SG users live on `sotravel.com` while the BE lives on `api.sotravel.me`.
// The `.sotravel.me`-scoped `sotravel_ref` cookie is dropped by browsers on
// that cross-site fetch (and even when stored, SameSite=Lax keeps it out of
// the subsequent XHR), so the BE never sees it on the signup call. Attach
// the localStorage code in the request body as the dominant attribution
// carrier — the BE's extractAffiliateFromSignupRequest already accepts it.
// Only do this when actually creating an account (allowAccountCreation=true)
// so existing-user re-OTPs don't burn the body slot.
export const sendOTPViaEmail = (email, allowAccountCreation = false) => {
  const affiliate_code =
    allowAccountCreation && typeof localStorage !== "undefined"
      ? localStorage.getItem("affiliate_code") || undefined
      : undefined;
  return unauthAxios.post("/v1/user/sendOTPViaEmail", {
    email,
    allowAccountCreation,
    ...(affiliate_code ? { affiliate_code } : {}),
  });
};

export const signInWithOTPViaEmail = (email, otp) => {
  return unauthAxios.post("/v1/user/signInWithOTPViaEmail", { email, otp });
};

export const getFollowingAndFollowers = () => {
  return authAxios.get("/v1/social/getFollowingAndFollowers");
};

export const joinCampaign = (campaign_id, user_id) => {
  return authAxios.post("/v1/user/joinCampaign", {
    campaign_id: campaign_id,
    user_id: user_id,
  });
};

export const hasJoinedCampaign = (campaign_id) => {
  return authAxios.get("/v1/user/hasJoinedCampaign", {
    params: {
      campaign_id: campaign_id,
    },
  });
};

export const getDuckProfile = (user_id) => {
  return unauthAxios.post(`/v1/duck/getDuckProfile`, {
    user_id: user_id,
  });
};

export const getDuckReviews = (user_id) => {
  return unauthAxios.post(`/v1/duck/getDuckReviews`, {
    user_id: user_id,
  });
};

export const getUpgradeStatus = () => {
  return authAxios.get("/v1/duck/upgrade/status");
};

export const triggerManualUpgrade = () => {
  return authAxios.post("/v1/duck/upgrade/trigger");
};

export const getUserFollowingFollowers = async () => {
  const res = await authAxios.get("/v1/social/getFollowingAndFollowers");
  return res.data;
};
